import { Badge, Button, DataTable, PageHeader, Skeleton, useToast } from '@zyxantech/tux'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateCustomerMutation, useGetCustomersQuery } from '@/api/partners.api'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { usePermission } from '@/hooks/usePermission'
import { formatTimestamp } from '@/utils/format'
import { buildPath } from '@/utils/url'
import { NewCustomerDialog } from './components/NewCustomerDialog'
import { useMyPartner } from './hooks/useMyPartner'

export function CustomersPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const canCreate = usePermission(RESOURCE.PARTNER, ACTION.CREATE)
  const [showNew, setShowNew] = useState(false)

  const { partner, isLoading: partnerLoading } = useMyPartner()
  const { data, isLoading, error, refetch } = useGetCustomersQuery(
    { id: partner?.id ?? '' },
    { skip: !partner },
  )
  const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation()

  const handleCreate = async (tenantName: string, tenantSlug: string) => {
    if (!partner) return
    try {
      await createCustomer({ id: partner.id, body: { tenantName, tenantSlug } }).unwrap()
      toast.show('Customer provisioned', 'success')
      setShowNew(false)
    } catch {
      toast.show('Failed to provision customer', 'error')
    }
  }

  if (partnerLoading || isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="25%" />
        <Skeleton height={300} />
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} retry={() => void refetch()} />
      </div>
    )
  }

  const customers = data?.items ?? []

  return (
    <div className="p-6">
      <PageHeader
        title="Customers"
        subtitle={partner ? `Tenants under ${partner.name}` : 'Customer tenants'}
        actions={
          canCreate ? (
            <Button variant="primary" icon={<Plus size={14} />} onClick={() => setShowNew(true)}>
              New customer
            </Button>
          ) : undefined
        }
      />

      {customers.length === 0 ? (
        <EmptyState
          title="No customers yet"
          message="Provision a customer tenant to get started."
          action={
            canCreate ? (
              <Button variant="primary" icon={<Plus size={14} />} onClick={() => setShowNew(true)}>
                New customer
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          columns={[
            {
              key: 'name',
              header: 'Name',
              accessor: (c) => <span className="font-medium text-text-primary">{c.name}</span>,
            },
            {
              key: 'slug',
              header: 'Slug',
              accessor: (c) => (
                <span className="font-mono text-xs text-text-muted">{c.slug}</span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              accessor: (c) => (
                <Badge variant={c.status === 'active' ? 'success' : 'default'}>{c.status}</Badge>
              ),
            },
            {
              key: 'createdAt',
              header: 'Created',
              accessor: (c) => (
                <span className="text-sm text-text-secondary">{formatTimestamp(c.createdAt)}</span>
              ),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              accessor: (c) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(buildPath(ROUTES.CUSTOMER_DETAIL, { id: c.id }))
                  }}
                >
                  View
                </Button>
              ),
            },
          ]}
          data={customers}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(buildPath(ROUTES.CUSTOMER_DETAIL, { id: c.id }))}
        />
      )}

      {showNew && (
        <NewCustomerDialog
          isLoading={isCreating}
          onCreate={handleCreate}
          onClose={() => setShowNew(false)}
        />
      )}
    </div>
  )
}
